import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AuthContext } from "../context/AuthContext";
import "./styles/DailyTask.css";

const REVIEW_ROLES = ["admin", "super-admin", "manager", "finance-head"];

export default function DailyTaskPage() {
  const { user } = useContext(AuthContext);

  const role = String(user?.role || "").toLowerCase();
  const isReviewer = REVIEW_ROLES.includes(role);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const toId = useCallback((v) => String(v || "").trim(), []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isWithinDays = (date, days = 7) => {
    if (!date) return false;
    return Date.now() - new Date(date).getTime() <= days * 24 * 60 * 60 * 1000;
  };

  const canSelfModify = (row) => {
    if (!row?._id) return false;
    const rowUserId = String(row?.user || "");
    return rowUserId === String(user?._id || "") && isWithinDays(row.entryDate, 7);
  };

  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);

  const showToast = useCallback((type, msg, duration = 2200) => {
    setToast({ type, msg });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), duration);
  }, []);

  useEffect(() => {
    return () => toastRef.current && clearTimeout(toastRef.current);
  }, []);

  const [people, setPeople] = useState([]);
  const [usersError, setUsersError] = useState("");

  const fetchUsers = useCallback(async () => {
    if (!isReviewer) return;
    try {
      const r = await axios.get("/api/users", authHeader);
      setPeople(Array.isArray(r.data) ? r.data : []);
      setUsersError("");
    } catch (e) {
      setPeople([]);
      setUsersError(
        e?.response?.data?.message || e.message || "Failed to load users"
      );
    }
  }, [authHeader, isReviewer]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const [qSearch, setQSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [onlyMyEntries, setOnlyMyEntries] = useState(false);
  const [todayOnly, setTodayOnly] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");

  const loadTasks = useCallback(async () => {
    setLoadingList(true);
    setListError("");

    try {
      const r = await axios.get("/api/daily-tasks", authHeader);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setRows([]);
      setListError(e?.response?.data?.message || e.message || "Failed to load");
    } finally {
      setLoadingList(false);
    }
  }, [authHeader]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskText, setTaskText] = useState("");
  const [entryDate, setEntryDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const resetAddForm = () => {
    setTaskText("");
    setEntryDate(new Date().toISOString().slice(0, 10));
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!taskText.trim()) {
      return showToast("error", "Please write your task");
    }

    setSaving(true);
    try {
      await axios.post(
        "/api/daily-tasks",
        {
          taskText: taskText.trim(),
          entryDate,
        },
        authHeader
      );
      resetAddForm();
      setDrawerOpen(false);
      await loadTasks();
      showToast("success", "Task saved ✅");
    } catch (err) {
      showToast(
        "error",
        err?.response?.data?.message || err.message || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");

  const openEdit = (row) => {
    if (!canSelfModify(row)) {
      return showToast(
        "error",
        "You can edit only your own entries within 7 days."
      );
    }
    setEditing(row);
    setEditText(row?.taskText || "");
  };

  const closeEdit = () => {
    setEditing(null);
    setEditText("");
  };

  const submitEdit = async () => {
    if (!editing?._id) return;
    if (!editText.trim()) {
      return showToast("error", "Task text is required");
    }

    try {
      await axios.patch(
        `/api/daily-tasks/${editing._id}`,
        { taskText: editText.trim() },
        authHeader
      );
      closeEdit();
      await loadTasks();
      showToast("success", "Updated ✅");
    } catch (err) {
      showToast(
        "error",
        err?.response?.data?.message || err.message || "Update failed"
      );
    }
  };

  const handleDelete = async (row) => {
    if (!row?._id) return;

    if (!canSelfModify(row)) {
      return showToast(
        "error",
        "You can delete only your own entries within 7 days."
      );
    }

    if (!window.confirm("Delete this entry?")) return;

    try {
      await axios.delete(`/api/daily-tasks/${row._id}`, authHeader);
      await loadTasks();
      showToast("success", "Deleted ✅");
    } catch (err) {
      showToast(
        "error",
        err?.response?.data?.message || err.message || "Delete failed"
      );
    }
  };

  const selectedEffectiveUserId =
    isReviewer && onlyMyEntries
      ? String(user?._id || "")
      : String(selectedUserId || "");

  const filteredRows = useMemo(() => {
    let data = [...rows];

    if (isReviewer) {
      if (selectedEffectiveUserId) {
        data = data.filter(
          (r) => String(r?.user || "") === selectedEffectiveUserId
        );
      }
    } else {
      data = data.filter(
        (r) => String(r?.user || "") === String(user?._id || "")
      );
    }

    if (selectedDate) {
      const start = new Date(`${selectedDate}T00:00:00`).getTime();
      const end = new Date(`${selectedDate}T23:59:59.999`).getTime();

      data = data.filter((r) => {
        const t = new Date(r.entryDate || r.createdAt || 0).getTime();
        return t >= start && t <= end;
      });
    }

    if (from) {
      const fromTime = new Date(`${from}T00:00:00`).getTime();
      data = data.filter(
        (r) => new Date(r.entryDate || r.createdAt || 0).getTime() >= fromTime
      );
    }

    if (to) {
      const toTime = new Date(`${to}T23:59:59`).getTime();
      data = data.filter(
        (r) => new Date(r.entryDate || r.createdAt || 0).getTime() <= toTime
      );
    }

    if (todayOnly) {
      const today = new Date();
      const start = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      ).getTime();
      const end = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      ).getTime();

      data = data.filter((r) => {
        const t = new Date(r.entryDate || r.createdAt || 0).getTime();
        return t >= start && t <= end;
      });
    }

    const q = String(qSearch || "").trim().toLowerCase();
    if (q) {
      data = data.filter((r) => {
        const text = [
          r?.userName || "",
          r?.userRole || "",
          r?.taskText || "",
          fmtDate(r?.entryDate),
          fmtDateTime(r?.createdAt),
        ]
          .join(" | ")
          .toLowerCase();

        return text.includes(q);
      });
    }

    return data.sort(
      (a, b) =>
        new Date(b.entryDate || b.createdAt || 0).getTime() -
        new Date(a.entryDate || a.createdAt || 0).getTime()
    );
  }, [
    rows,
    isReviewer,
    selectedEffectiveUserId,
    user?._id,
    selectedDate,
    from,
    to,
    todayOnly,
    qSearch,
  ]);

  const summary = useMemo(() => {
    const totalEntries = filteredRows.length;
    const ownEntries = filteredRows.filter(
      (r) => String(r?.user || "") === String(user?._id || "")
    ).length;

    const todayStr = new Date().toDateString();
    const todayEntries = rows.filter(
      (r) =>
        new Date(r.entryDate || r.createdAt || "").toDateString() === todayStr
    ).length;

    const editableCount = filteredRows.filter((r) => canSelfModify(r)).length;

    return {
      totalEntries,
      ownEntries,
      todayEntries,
      editableCount,
    };
  }, [filteredRows, rows, user?._id]);

  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [qSearch, selectedUserId, onlyMyEntries, selectedDate, from, to, todayOnly]);

  const totalPages = useMemo(() => {
    const n = Math.ceil((filteredRows.length || 0) / pageSize);
    return n || 1;
  }, [filteredRows.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const currentEmployeeLabel = useMemo(() => {
    if (!isReviewer) return user?.username || "Me";
    if (onlyMyEntries) return user?.username || "My Entries";
    if (selectedUserId) {
      return (
        people.find((p) => String(p._id) === String(selectedUserId))?.username ||
        "Selected Employee"
      );
    }
    return "All Employees";
  }, [isReviewer, onlyMyEntries, selectedUserId, people, user?.username]);

  const safeFilePart = (s) =>
    String(s || "")
      .trim()
      .replace(/[^\w\-]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60);

  const buildExportRows = () =>
    filteredRows.map((r) => ({
      Date: fmtDate(r.entryDate),
      Employee: r?.userName || "-",
      Role: r?.userRole || "-",
      Task: r?.taskText || "-",
      CreatedAt: fmtDateTime(r?.createdAt),
      EditedAt: r?.editedAt ? fmtDateTime(r.editedAt) : "—",
    }));

  const handleExport = (kind) => {
    const exportRows = buildExportRows();
    if (!exportRows.length) return showToast("info", "No data to export.");

    const fileBase = [
      "Daily_Task_Log",
      safeFilePart(currentEmployeeLabel),
      safeFilePart(
        selectedDate ? selectedDate : todayOnly ? "today" : from || "start"
      ),
      "to",
      safeFilePart(
        selectedDate ? selectedDate : todayOnly ? "today" : to || "today"
      ),
    ]
      .filter(Boolean)
      .join("_");

    if (kind === "xlsx") {
      const ws = XLSX.utils.json_to_sheet(exportRows);
      ws["!cols"] = [
        { wch: 14 },
        { wch: 20 },
        { wch: 18 },
        { wch: 80 },
        { wch: 22 },
        { wch: 22 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Daily Tasks");
      XLSX.writeFile(wb, `${fileBase}.xlsx`);
      return showToast("success", "Excel exported ✅");
    }

    if (kind === "pdf") {
      const doc = new jsPDF("l", "pt", "a4");
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 36;

      doc.setFontSize(16);
      doc.text("Daily Task Log", marginX, 34);

      doc.setFontSize(10);
      [
        `Generated: ${new Date().toLocaleString("en-IN", { hour12: true })}`,
        `Employee: ${currentEmployeeLabel}`,
        `Date Range: ${
          selectedDate
            ? selectedDate
            : todayOnly
            ? "Today"
            : `${from || "Start"} → ${to || "Today"}`
        }`,
        `Rows: ${filteredRows.length}`,
      ].forEach((t, i) => doc.text(t, marginX, 52 + i * 14));

      autoTable(doc, {
        startY: 120,
        head: [["Date", "Employee", "Role", "Task", "Created", "Edited"]],
        body: filteredRows.map((r) => [
          fmtDate(r.entryDate),
          r?.userName || "-",
          r?.userRole || "-",
          r?.taskText || "-",
          fmtDateTime(r?.createdAt),
          r?.editedAt ? fmtDateTime(r.editedAt) : "—",
        ]),
        theme: "striped",
        margin: { left: marginX, right: marginX, bottom: 34 },
        styles: {
          fontSize: 9,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: [11, 26, 51],
          textColor: 255,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 100 },
          2: { cellWidth: 90 },
          3: { cellWidth: 340 },
          4: { cellWidth: 110 },
          5: { cellWidth: 110 },
        },
        didDrawPage: (data) => {
          const pageCount = doc.getNumberOfPages();
          doc.setFontSize(9);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageW - marginX - 90,
            pageH - 16
          );
        },
      });

      doc.save(`${fileBase}.pdf`);
      return showToast("success", "PDF exported ✅");
    }
  };

  const applyNow = async () => {
    await loadTasks();
    showToast("info", "Applied");
  };

  const userOptions = (people || []).map((p) => (
    <option key={p._id} value={p._id}>
      {p.username}
    </option>
  ));

  return (
    <div className="tech-page daily-task-page">
      {toast && (
        <div
          className={`toast toast-${toast.type}`}
          onClick={() => setToast(null)}
        >
          {toast.msg}
        </div>
      )}

      <section className="cashbook-topbar">
        <div className="cashbook-title">
          <div className="h">
            {isReviewer ? "Daily Task Review" : "My Daily Tasks"}
          </div>
          <div className="p">
            {isReviewer
              ? "View employee work entries with filters and exports"
              : "Write what you did and manage entries for 7 days"}
          </div>
        </div>

        <div className="cashbook-top-actions">
          {isReviewer && (
            <button
              className={`btn ${onlyMyEntries ? "ghost active-toggle" : ""}`}
              type="button"
              onClick={() => {
                setOnlyMyEntries((prev) => !prev);
                setSelectedUserId("");
              }}
              title="Show only my entries"
            >
              {onlyMyEntries ? "Showing My Entries" : "Only My Entries"}
            </button>
          )}

          <button
            className="btn primary"
            type="button"
            onClick={() => setDrawerOpen(true)}
          >
            + Add Task Entry
          </button>
        </div>
      </section>

      <section className="card cashbook-panel">
        <div className="cashbook-grid">
          <div className="field">
            <label>Search</label>
            <input
              className="input"
              value={qSearch}
              onChange={(e) => setQSearch(e.target.value)}
              placeholder="employee / role / task / date..."
            />
          </div>

          {isReviewer ? (
            <div className="field">
              <label>Employee</label>
              <select
                className="input"
                value={onlyMyEntries ? user?._id || "" : selectedUserId}
                onChange={(e) => setSelectedUserId(toId(e.target.value))}
                disabled={onlyMyEntries}
              >
                <option value="">
                  {onlyMyEntries ? "My entries only" : "All employees"}
                </option>
                {!onlyMyEntries && userOptions}
              </select>
            </div>
          ) : (
            <div className="field">
              <label>Employee</label>
              <input className="input" value={user?.username || "-"} disabled />
            </div>
          )}

          <div className="field">
            <label>Select Day</label>
            <input
              className="input"
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setTodayOnly(false);
              }}
            />
          </div>

          <div className="field">
            <label>From</label>
            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className="field">
            <label>To</label>
            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        <div className="cashbook-actions-row">
          <div className="left">
            {listError ? <div className="inline-error">{listError}</div> : null}
            {usersError ? <div className="inline-hint">{usersError}</div> : null}
          </div>

          <div className="right">
            <button
              className={`btn ${todayOnly ? "ghost active-toggle" : ""}`}
              type="button"
              onClick={() => {
                setTodayOnly((prev) => {
                  const next = !prev;
                  if (next) setSelectedDate("");
                  return next;
                });
              }}
            >
              {todayOnly ? "Showing Today's Entries" : "Today's Entries"}
            </button>

            <button
              className="btn"
              type="button"
              onClick={() => {
                setQSearch("");
                setFrom("");
                setTo("");
                setSelectedDate("");
                setTodayOnly(false);
                if (isReviewer) setSelectedUserId("");
                setOnlyMyEntries(false);
                showToast("info", "Cleared");
              }}
            >
              Clear
            </button>

            <button className="btn" type="button" onClick={applyNow}>
              Apply
            </button>

            <button
              className="btn ghost"
              type="button"
              onClick={() => handleExport("pdf")}
            >
              PDF
            </button>

            <button
              className="btn ghost"
              type="button"
              onClick={() => handleExport("xlsx")}
            >
              XLSX
            </button>
          </div>
        </div>
      </section>

      <section className="cashbook-stats">
        <div className="stat">
          <div className="k">Total Entries</div>
          <div className="v">{summary.totalEntries}</div>
        </div>
        <div className="stat">
          <div className="k">My Entries</div>
          <div className="v">{summary.ownEntries}</div>
        </div>
        <div className="stat">
          <div className="k">Today</div>
          <div className="v">{summary.todayEntries}</div>
        </div>
        <div className="stat highlight">
          <div className="k">Editable Now</div>
          <div className="v">{summary.editableCount}</div>
        </div>
      </section>

      <section className="card table-card compact-table">
        <div
          className="table-header"
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <div className="title">Entries</div>
            <div className="count">
              {filteredRows.length} row{filteredRows.length === 1 ? "" : "s"}
            </div>
            {loadingList ? <div className="count">Loading...</div> : null}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              className="input"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) || 15)}
              title="Rows per page"
              style={{ width: 110 }}
            >
              <option value={10}>10 / page</option>
              <option value={15}>15 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>

            <button
              className="btn mini"
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              title="First"
            >
              «
            </button>
            <button
              className="btn mini"
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              title="Previous"
            >
              ‹
            </button>

            <div
              className="muted"
              style={{
                fontSize: 12,
                fontWeight: 900,
                minWidth: 110,
                textAlign: "center",
              }}
            >
              Page {page} / {totalPages}
            </div>

            <button
              className="btn mini"
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              title="Next"
            >
              ›
            </button>
            <button
              className="btn mini"
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              title="Last"
            >
              »
            </button>
          </div>
        </div>

        <div className="table-scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Role</th>
                <th>Task</th>
                <th>Created</th>
                <th>Edited</th>
                <th className="center">Action</th>
              </tr>
            </thead>

            <tbody>
              {pagedRows.map((r, i) => {
                const canEdit = canSelfModify(r);

                return (
                  <tr key={r._id || `row-${(page - 1) * pageSize + i}`}>
                    <td className="nowrap">{fmtDate(r.entryDate)}</td>
                    <td className="cell-strong">{r?.userName || "-"}</td>
                    <td className="nowrap">{r?.userRole || "-"}</td>
                    <td className="task-text-cell">{r?.taskText || "-"}</td>
                    <td className="nowrap">{fmtDateTime(r.createdAt)}</td>
                    <td className="nowrap">
                      {r?.editedAt ? fmtDateTime(r.editedAt) : "—"}
                    </td>
                    <td className="center">
                      {canEdit ? (
                        <div className="row-actions">
                          <button
                            className="btn-link"
                            type="button"
                            onClick={() => openEdit(r)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-link danger"
                            type="button"
                            onClick={() => handleDelete(r)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <span className="row-actions-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!pagedRows.length && (
                <tr>
                  <td
                    colSpan="7"
                    className="center muted"
                    style={{ padding: 16, fontWeight: 900 }}
                  >
                    No entries
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)}>
          <div className="card modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="title">Add Daily Task</div>
              <button
                className="btn mini"
                type="button"
                onClick={() => {
                  resetAddForm();
                  setDrawerOpen(false);
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAdd} className="modal-form">
              <div className="field">
                <label>Entry Date</label>
                <input
                  className="input"
                  type="date"
                  value={entryDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setEntryDate(e.target.value)}
                  required
                />
              </div>

              <div className="field">
                <label>What did you do?</label>
                <textarea
                  className="input task-textarea"
                  value={taskText}
                  onChange={(e) => setTaskText(e.target.value)}
                  placeholder="Write your work details here..."
                  required
                />
              </div>

              <div className="muted small-note">
                You can choose today or a past date. Future dates are not allowed.
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn mini"
                  onClick={() => setDrawerOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn primary mini"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="drawer-overlay" onClick={closeEdit}>
          <div className="card modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="title">Edit Daily Task</div>
              <button className="btn mini" type="button" onClick={closeEdit}>
                ✕
              </button>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <textarea
                className="input task-textarea"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Update your task..."
              />

              <div className="modal-actions">
                <button className="btn mini" type="button" onClick={closeEdit}>
                  Cancel
                </button>
                <button
                  className="btn primary mini"
                  type="button"
                  onClick={submitEdit}
                >
                  Save
                </button>
              </div>

              <div className="muted" style={{ fontSize: 11, fontWeight: 900 }}>
                Edit and delete are allowed only for your own entries within 7 days.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}